import './DodMarketFilterValuePicker.scss';
import React, { useEffect, useMemo, useState, useContext, useCallback, useRef } from 'react';
import classnames from 'classnames';
import { DodFilterType, DodMarketFilterType } from '@/components/DodConfigEditor/types';
import { DodFilters } from '@/types/DodRun';
import { ByzzerChangeEventHandler } from '@byzzer/ui-components';
import { DodFilterValuePicker, DodFilterValuePickerRef, DodValueOption } from '@/components/DodConfigEditor/common';
import { isMarketDisabled, isMarketHidden, useMarketService } from '@/services/market.service';
import { filterValuesToStrings } from '@/components/DodConfigEditor/common/utils';
import { MarketPickerContext, MarketPickerNode } from '@/components/MarketPicker';
import { RunConfigMarket } from '@/types/ReportRun';
import { isAsyncFunction } from '@/utils';
import { DodMarketTree } from '../DodMarketTreePicker/DodMarketTree';
import { useUser } from '@/contexts/UserContext';
import { AdvancedSearchFilters, MarketTab } from '../DodMarketFilterBuilder';
import { unionBy } from 'lodash';
import {alertSelectionLimitExceed} from '@/components/DodConfigEditor/common/utils'
import { useCategoryService } from '@/services/category.service';
import { useDodWizard } from '@/components/DodConfigEditor/DodRunConfigWizard/DodWizardContext';
import useAllExceptProductFields from '@/utils/useAllExceptProductFields';
import { useTenantApi } from '@/hooks/useTenantApi';

const baseClassName = 'dod-market-filter-value-picker';

export type DodMarketFilterValuePickerProps = {
    name?: string;
    className?: string;
    filterType: DodFilterType<DodMarketFilterType>;
    filters: DodFilters;
    value: RunConfigMarket[] | string[] | 'all';
    onChange?: ByzzerChangeEventHandler<RunConfigMarket[] | string[] | 'all'>;
    onApply?: ByzzerChangeEventHandler<RunConfigMarket[] | string[] | 'all'>;
    actions?: ActionConfig[];
    pickerRef?: React.Ref<DodFilterValuePickerRef>;
    includeActions?: boolean;
    filterText?: string;
    activeMarketFolderTab?: MarketTab;
    advancedSearchFilters?: AdvancedSearchFilters;
    limit?: number;    
    inSummedMode?:boolean;
    updateAccessibleMarketFolders?: (folders) => void
} & Partial<Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>>;

type FilterValueConfig = {
    getOptions(values: DodFilters, data: any): Promise<DodValueOption[]> | DodValueOption[] | void;
    valueToStrings?(value: any): string[];
    stringsToValue?(strings: string[]): any;
    filterValues?(searchTerm: string): DodValueOption[];
};

function defaultValueToStrings(value: RunConfigMarket[] | string[] | 'all'): string[] {
    if (value === 'all') return ['All'];
    if (!Array.isArray(value)) return [];
    let marketNames = value?.map((market: any) => {
        if (typeof market === 'string') return market;
        if (market) {
            return market.name;
        }
        return '';
    });
    return marketNames;
}

export function DodMarketFilterValuePicker({
    name,
    className,
    filterType,
    filters,
    value,
    onChange,
    onApply,
    pickerRef,
    filterText = '',
    advancedSearchFilters,
    limit = Infinity,
    activeMarketFolderTab,  
    inSummedMode,  
    ...props
}: DodMarketFilterValuePickerProps) {
    const {
        getMarketsOptionsByMarketFilterType,
        getRunConfigMarketForMarketName,
        filterMarketOptions,
        getMarketTypes,
        getMarketChannels,
        getMarketParentCompanies,
        getParentCompanyMarketNodesByName,
        getChildMarketTypes,
        getApplicableMarketCharacteristicValues,
    } = useMarketService();
    const [markets, setMarkets] = useState<RunConfigMarket[]>([]);
    const [options, setOptions] = useState<DodValueOption[]>([]);
    const {
        categories: userCategories,
        company,
        features: { enableLimitedMarketSubscription },
        accessibleMasterCompanies,
    } = useUser();
    const { 
        requiredMasterCompany, 
        requiredMarketGroup, 
        requireRemainingMarket, 
        includePseudoRemainingMarkets, 
        showRemainingMarkets, 
        hideDisabledMarkets, 
        hideNonPromoMarkets 
    } = useContext(MarketPickerContext);    
    const {allExceptSelectionFields} = useDodWizard();
    const { categories: allExceptProductFieldsCategoriesRaw } = useAllExceptProductFields(filters, allExceptSelectionFields);
    const allExceptProductFieldsCategories = useMemo(() => allExceptProductFieldsCategoriesRaw, [filters?.categories?.values, filters?.categories?.summedSelections, allExceptSelectionFields]);

    const { getAllCategoriesByDepartmentsAndSuperCategories } = useCategoryService();

    // BYZ-19583 - a summed selection may store the 'all' sentinel instead of a concrete category list; passing
    // the literal 'all' straight into the applicability service breaks category-driven disabling, so resolve it
    // to the real department/super-category category list first, same as `getPreviousCategoryContext` does.
    const resolvedSummedCategories = useMemo(
        () =>
            filters?.categories?.summedSelections?.some((ss) => ss.values === 'all')
                ? getAllCategoriesByDepartmentsAndSuperCategories(filters['departments'], filters['superCategories'])
                : filters?.categories?.summedSelections?.map((item) => item.values).flat() as string[],
        [filters?.categories?.summedSelections, filters?.departments, filters?.superCategories]
    );
    const { getCharacteristicValuesForCategories } = useTenantApi();

    const checkMarketDisabled = useCallback((market: MarketNode): boolean => {
        return isMarketDisabled(market, {
            requiredMarketGroup,
            requiredMasterCompany,
            requireRemainingMarket,
            enableLimitedMarketSubscription,
            accessibleMasterCompanies,            
            purchasedMarketKeys:company?.purchasedMarketKeys,  
            isMarketSummedMode: inSummedMode
        });
    }, [requiredMarketGroup, requiredMasterCompany, requireRemainingMarket])

    const checkMarketHidden = useCallback((market: MarketNode): boolean => {
        return isMarketHidden(market, {
            includePseudoRemainingMarkets,
            requireRemainingMarket,
            showRemainingMarkets,
            hideDisabledMarkets,
            hideNonPromoMarkets,
            requiredMarketGroup,
            requiredMasterCompany,
            enableLimitedMarketSubscription,
            accessibleMasterCompanies,
            purchasedMarketKeys:company?.purchasedMarketKeys, 
        });
    }, [includePseudoRemainingMarkets, requiredMarketGroup, requiredMasterCompany, requireRemainingMarket, showRemainingMarkets, hideDisabledMarkets, hideNonPromoMarkets])

    const filterValueConfigs = useMemo<Record<DodMarketFilterType, FilterValueConfig>>(
        () => ({
            all: {
                getOptions(filters) {
                    // when filter type is 'all' or 'accounts', <DodMarketTreePicker /> is rendered
                    // Hence we don't require getOptions() in these cases
                },
                filterValues(searchTerm) {
                    setLoading(true);
                    try {
                        const marketsNodes = filterMarketOptions({
                            includeFmcgRetailers: true,
                            includeSpecialityRetailers: true,
                            includeGeographyMarkets: true,
                            includeTotalUSMarkets: true,
                            includePanelTotal: false,
                            includePannelByChannel: false,
                            searchTerm: searchTerm,
                            categories: allExceptProductFieldsCategories,
                            purchasedMarketKeys: company?.purchasedMarketKeys,
                            isDodMarketPicker: true,
                            hasSummedCategories: filters?.categories?.summedSelections.length ? true : false,
                            summedCategories: filters?.categories?.summedSelections.map((item) => item.values).flat(),
                            nonSummedCategories: allExceptProductFieldsCategories,
                            isMarketSummedMode:inSummedMode,
                        });                       
                        const result = marketsNodes.filter((market) => market.selectable && !checkMarketHidden(market))
                            .map((market) => ({
                                text: market.name,
                                display: <MarketPickerNode marketNode={market} isMarketSummedMode={inSummedMode} />,
                                value: market.name,
                                disabled: checkMarketDisabled(market),
                            }));
                        return result;
                    } catch (e) {
                        return [] as DodValueOption[];
                    } finally {
                        setLoading(false);
                    }
                },
            },
            channels: {
                async getOptions(filters) {
                    const markets = await getMarketsOptionsByMarketFilterType({
                        categories: allExceptProductFieldsCategories,
                        marketType: 'Channel',
                        purchasedMarketKeys: company?.purchasedMarketKeys,
                        isDodMarketPicker: true,
                        hasSummedCategories: filters?.categories?.summedSelections.length ? true : false,
                        summedCategories: filters?.categories?.summedSelections.map((item) => item.values).flat(),
                        nonSummedCategories: allExceptProductFieldsCategories,
                        isMarketSummedMode:inSummedMode,
                    });
                    return markets.filter((market) => !checkMarketHidden(market.data!))
                        .map((market) => ({
                            text: market.display,
                            display: <MarketPickerNode marketNode={market.data!} />,
                            value: market.display,
                            disabled: checkMarketDisabled(market.data!),
                        }));
                },
            },
            accounts: {
                getOptions(filters) {
                    // when filter type is 'all' or 'accounts', <DodMarketTreePicker /> is rendered
                    // Hence we don't require getOptions() in these cases
                },
                filterValues(searchTerm) {
                    setLoading(true);
                    try {
                        const marketsNodes = filterMarketOptions({
                            includeFmcgRetailers: true,
                            includeSpecialityRetailers: true,
                            includeGeographyMarkets: false,
                            includeTotalUSMarkets: false,
                            includePanelTotal: false,
                            includePannelByChannel: false,
                            searchTerm: searchTerm,
                            categories: allExceptProductFieldsCategories,
                            purchasedMarketKeys: company?.purchasedMarketKeys,
                            isDodMarketPicker: true,
                            hasSummedCategories: filters?.categories?.summedSelections.length ? true : false,
                            summedCategories: filters?.categories?.summedSelections.map((item) => item.values).flat(),
                            nonSummedCategories: allExceptProductFieldsCategories,
                            isMarketSummedMode:inSummedMode,
                        });
                        return marketsNodes.filter((market) => market.selectable && !checkMarketHidden(market))
                            .map((market) => ({
                                text: market.name,
                                display: <MarketPickerNode marketNode={market} isMarketSummedMode={inSummedMode}/>,
                                value: market.name,
                                disabled: checkMarketDisabled(market),
                            }));
                    } catch (e) {
                        return [] as DodValueOption[];
                    } finally {
                        setLoading(false);
                    }
                },
            },
            stateLines: {
                async getOptions(filters) {
                    const markets = await getMarketsOptionsByMarketFilterType({
                        categories: allExceptProductFieldsCategories,
                        marketType: 'Stateline',
                        purchasedMarketKeys: company?.purchasedMarketKeys,
                        isDodMarketPicker: true,
                        hasSummedCategories: filters?.categories?.summedSelections.length ? true : false,
                        summedCategories: filters?.categories?.summedSelections.map((item) => item.values).flat(),
                        nonSummedCategories: allExceptProductFieldsCategories,
                        isMarketSummedMode:inSummedMode,
                    });
                    return markets.filter((market) => !checkMarketHidden(market.data!))
                        .map((market) => ({
                            text: market.display,
                            display: <MarketPickerNode marketNode={market.data!} />,
                            value: market.display,
                            disabled: checkMarketDisabled(market.data!),
                        }));
                },
            },
            majorMarkets: {
                async getOptions(filters) {
                    const markets = await getMarketsOptionsByMarketFilterType({
                        categories: allExceptProductFieldsCategories,
                        marketType: 'Major Market',
                        purchasedMarketKeys: company?.purchasedMarketKeys,
                        isDodMarketPicker: true,
                        hasSummedCategories: filters?.categories?.summedSelections.length ? true : false,
                        summedCategories: filters?.categories?.summedSelections.map((item) => item.values).flat(),
                        nonSummedCategories: allExceptProductFieldsCategories,
                        isMarketSummedMode:inSummedMode,
                    });
                    return markets.filter((market) => !checkMarketHidden(market.data!))
                        .map((market) => ({
                            text: market.display,
                            display: <MarketPickerNode marketNode={market.data!} />,
                            value: market.display,
                            disabled: checkMarketDisabled(market.data!),
                        }));
                },
            },
            regions: {
                async getOptions(filters) {
                    const markets = await getMarketsOptionsByMarketFilterType({
                        categories: allExceptProductFieldsCategories,
                        marketType: 'Region',
                        purchasedMarketKeys: company?.purchasedMarketKeys,
                        isDodMarketPicker: true,
                        hasSummedCategories: filters?.categories?.summedSelections.length ? true : false,
                        summedCategories: filters?.categories?.summedSelections.map((item) => item.values).flat(),
                        nonSummedCategories: allExceptProductFieldsCategories,
                        isMarketSummedMode:inSummedMode,
                    });
                    return markets.filter((market) => !checkMarketHidden(market.data!))
                        .map((market) => ({
                            text: market.display,                            
                            display: <MarketPickerNode marketNode={market.data!} />,
                            value: market.display,
                            disabled: checkMarketDisabled(market.data!),
                        }));
                },
            },
            divisions: {
                async getOptions(filters) {
                    const markets = await getMarketsOptionsByMarketFilterType({
                        categories: allExceptProductFieldsCategories,
                        marketType: 'Division',
                        purchasedMarketKeys: company?.purchasedMarketKeys,
                        isDodMarketPicker: true,
                        hasSummedCategories: filters?.categories?.summedSelections.length ? true : false,
                        summedCategories: filters?.categories?.summedSelections.map((item) => item.values).flat(),
                        nonSummedCategories: allExceptProductFieldsCategories,
                        isMarketSummedMode:inSummedMode,
                    });
                    return markets.filter((market) => !checkMarketHidden(market.data!))
                        .map((market) => ({
                            text: market.display,                            
                            display: <MarketPickerNode marketNode={market.data!} />,
                            value: market.display,
                            disabled: checkMarketDisabled(market.data!),
                        }));
                },
            },
            marketCharacteristics: {
                async getOptions(filters, { id }) {
                    if (id === 'parentRetailer' || id === 'parentCompany') {
                        const companyNames = getMarketParentCompanies();
                        const nodeByCompany = getParentCompanyMarketNodesByName({
                            categories: allExceptProductFieldsCategories,
                            purchasedMarketKeys: company?.purchasedMarketKeys,
                            isDodMarketPicker: true,
                            hasSummedCategories: filters?.categories?.summedSelections.length ? true : false,
                            summedCategories: filters?.categories?.summedSelections.map((item) => item.values).flat(),
                            nonSummedCategories: allExceptProductFieldsCategories,
                            isMarketSummedMode: inSummedMode,
                        });

                        // Get applicable parent retailers based on selected categories
                        const applicableCharacteristics = getApplicableMarketCharacteristicValues({
                            categories: allExceptProductFieldsCategories,
                            purchasedMarketKeys: company?.purchasedMarketKeys,
                            hasSummedCategories: filters?.categories?.summedSelections.length ? true : false,
                            summedCategories: resolvedSummedCategories,
                            nonSummedCategories: allExceptProductFieldsCategories,
                            isMarketSummedMode: inSummedMode,
                            characteristicIds: ['parentRetailer', 'parentCompany'],
                        });

                        const applicableParentRetailers = new Set(applicableCharacteristics[id] || []);
                        // BYZ-19583 - category coverage only constrains retailers once categories are actually
                        // selected; `applicableParentRetailers` legitimately comes back empty in two very
                        // different situations that must not be conflated:
                        //   1. no categories selected yet - nothing to constrain by, so nothing should be
                        //      disabled on account of category coverage.
                        //   2. categories selected but none of them are covered by ANY retailer - every
                        //      retailer should be disabled, exactly like the regular Markets picker does via
                        //      `addCategoryCoverage` (which disables a market whenever its categories don't
                        //      intersect the selection, even if that disables every market).
                        // The previous `applicableParentRetailers.size > 0` guard treated case 2 the same as
                        // case 1 and left every retailer enabled, unlike the Markets picker.
                        const hasCategorySelection = allExceptProductFieldsCategories.length > 0;

                        return companyNames.map((value) => {
                            const node = nodeByCompany[value];
                            // Disable parent retailers that are not applicable for the selected categories
                            const isNotApplicableForCategory = hasCategorySelection && !applicableParentRetailers.has(value);
                            return {
                                text: value,
                                value,
                                display: node ? <MarketPickerNode marketNode={node} /> : undefined,
                                disabled: isNotApplicableForCategory || (node ? checkMarketDisabled(node) : false),
                            };
                        });
                    }
                    let options: string[] = [];
                    if (id === 'marketType') {
                        options = getMarketTypes();
                    } else if (id === 'marketLevel') {
                        options = ['Parent Market', 'Child Market'];
                    } else if (id === 'marketTier') {
                        options = ['National', 'Regional'];
                    } else if (id === 'channel') {
                        options = getMarketChannels();
                    } else if (id === 'childMarketType') {
                        options = getChildMarketTypes();
                    } else {
                        options = await getCharacteristicValuesForCategories(allExceptProductFieldsCategories, id);
                    }

                    // BYZ-19583 - these ids are category-driven the same way parentRetailer/parentCompany are
                    // (see CATEGORY_DRIVEN_MARKET_CHARACTERISTIC_IDS in market.service.tsx), but unlike the
                    // retailer branch above, this branch never disabled anything for category coverage - it
                    // kept every value selectable regardless of the selected categories, which is why Markets
                    // correctly disabled markets with no category coverage while Market Characteristics didn't.
                    const CATEGORY_DRIVEN_IDS = ['marketType', 'marketLevel', 'marketTier', 'channel', 'childMarketType'];
                    if (CATEGORY_DRIVEN_IDS.includes(id)) {
                        const applicableCharacteristics = getApplicableMarketCharacteristicValues({
                            categories: allExceptProductFieldsCategories,
                            purchasedMarketKeys: company?.purchasedMarketKeys,
                            hasSummedCategories: filters?.categories?.summedSelections.length ? true : false,
                            summedCategories: resolvedSummedCategories,
                            nonSummedCategories: allExceptProductFieldsCategories,
                            isMarketSummedMode: inSummedMode,
                            characteristicIds: [id],
                        });
                        const applicableValues = new Set(applicableCharacteristics[id] || []);
                        const hasCategorySelectionForId = allExceptProductFieldsCategories.length > 0;
                        return options.map((value) => ({
                            text: value,
                            value,
                            disabled: hasCategorySelectionForId && !applicableValues.has(value),
                        }));
                    }

                    return options.map((value) => ({
                        text: value,
                        value,
                    }));
                },
            },
        }),
        [filterType.type, filterType.data?.id, checkMarketDisabled, checkMarketHidden, allExceptProductFieldsCategories, resolvedSummedCategories]
    );
    const [normalizedValue, setNormalizedValue] = useState<string[] | 'all'>();
    const [loading, setLoading] = useState<boolean>(false);

    // Tracks the previously active characteristic so we can tell a real
    // characteristic switch apart from the same characteristic's value prop
    // being re-delivered (e.g. after an async options load resolves).
    const prevCharacteristicIdRef = useRef<string | undefined>(filterType.data?.id);
    // BYZ-19404 - Tracks the previously delivered value so we can tell "the stored value has always been empty
    // and the prop is just a fresh `[]` literal from the parent's render" apart from "the stored value was just
    // emptied", which happens when a category change invalidates the characteristic and it gets cleared.
    const prevValueRef = useRef<typeof value>(value);

    useEffect(() => {
        if (activeMarketFolderTab === 'markets' || activeMarketFolderTab === 'characteristics') {
            (async () => {
                const { getOptions } = filterValueConfigs[filterType.type];
                setLoading(isAsyncFunction(getOptions));
                const options = await getOptions(filters, filterType.data);
                setOptions(options ?? []);
                setLoading(false);
            })();
        } else if (activeMarketFolderTab === 'search') {
            setOptions([]);
            setLoading(true);
            const marketsNodes: MarketNode[] = filterMarketOptions({
                includeFmcgRetailers: true,
                includeSpecialityRetailers: true,
                includeGeographyMarkets: true,
                includeTotalUSMarkets: true,
                includePanelTotal: false,
                includePannelByChannel: false,
                categories: filterValuesToStrings(filters.categories, userCategories),
                marketType: advancedSearchFilters?.marketType?.value,
                marketLevel: advancedSearchFilters?.marketLevel?.value,
                marketTier: advancedSearchFilters?.marketTier?.value,
                parentCompanies: advancedSearchFilters?.parentCompany?.map((parentCompany) => parentCompany.value),
                channel: advancedSearchFilters?.channel?.value,
                childMarketType: advancedSearchFilters?.childMarketType?.value,
                purchasedMarketKeys: company?.purchasedMarketKeys,
                isDodMarketPicker: true,
                hasSummedCategories: filters?.categories?.summedSelections.length ? true : false,
                summedCategories: filters?.categories?.summedSelections.map((item) => item.values).flat(),
                nonSummedCategories: filters?.categories.values as string[],
                isMarketSummedMode:inSummedMode,
            });
            const result = marketsNodes.filter((market) => market.selectable && !checkMarketHidden(market))
                .map((market) => ({
                    text: market.name,
                    display: <MarketPickerNode marketNode={market} isMarketSummedMode={inSummedMode}/>,
                    value: market.name,
                    disabled: checkMarketDisabled(market),
                    marketHasPartialApproval: market.hasPartialApproval, 
                }));
            setOptions(result as DodValueOption[]);
            setLoading(false);
        }
    }, [filterType, activeMarketFolderTab, filterValueConfigs, advancedSearchFilters, filters.categories, userCategories, company?.purchasedMarketKeys, checkMarketHidden, checkMarketDisabled]);


    useEffect(() => {
        if (markets.length) {
            // const allValues: ValueType[] = options.map(({ value }) => value);
            // const allMatched =
            //     intersection(
            //         allValues,
            //         markets.map((market) => market.name)
            //     ).length === allValues.length;
            // if (allMatched) {
            //     setNormalizedValue('all');
            // } else {
            const { valueToStrings = defaultValueToStrings } = filterValueConfigs[filterType.type];
            setNormalizedValue(valueToStrings(markets));
            // }
        } else {
            const { valueToStrings = defaultValueToStrings } = filterValueConfigs[filterType.type];
            setNormalizedValue(valueToStrings(markets));
        }
    }, [markets, filterType, filters, filterValueConfigs]);

    useEffect(() => {
        const characteristicChanged =
            filterType.type === 'marketCharacteristics' && prevCharacteristicIdRef.current !== filterType.data?.id;
        prevCharacteristicIdRef.current = filterType.data?.id;
        // BYZ-19404 - the parent builds this prop as `marketCharacteristics?.[id]?.values || []`, so an unselected
        // characteristic yields a brand new `[]` on every parent render. Only a transition from a non-empty
        // (or 'all') value to an empty one is a genuine external clear.
        const wasAlreadyEmpty = Array.isArray(prevValueRef.current) && prevValueRef.current.length === 0;
        prevValueRef.current = value;

        if (Array.isArray(value)) {
            // For market characteristics, only skip syncing from props when the value is empty
            // AND we're still on the same characteristic (an in-flight options load resolving late
            // shouldn't wipe selections the user hasn't applied yet). A real characteristic switch
            // must always sync, even to an empty value, otherwise the previous characteristic's
            // selection would incorrectly linger.
            // BYZ-19404 - `wasAlreadyEmpty` keeps that protection for the repeated-`[]` case while still
            // honouring the clear performed when a category change invalidates the characteristic.
            if (
                filterType.type === 'marketCharacteristics' &&
                value.length === 0 &&
                !characteristicChanged &&
                wasAlreadyEmpty
            ) {
                // Don't reset local selection - user may be making selections that haven't been applied yet
                return;
            }
            setMarkets(value as RunConfigMarket[]);
        } else if (value === 'all' && filterType.type === 'marketCharacteristics') {
            // For market characteristics, when value is 'all', set normalizedValue directly
            // This ensures the DodFilterValuePicker receives 'all' and maintains correct includeAll state
            setNormalizedValue('all');
        } else if (value === 'all') {
            // For market types that use DodMarketTree, 'all' shouldn't reach here
            // Keep the current behavior
        } else {
            setMarkets([]);
        }
    }, [value, filterType.type, filterType.data?.id]);

    const categories = useMemo<string[]>(() => {
        if (
            filters.categories.values === 'all' ||
            filters.categories.summedSelections.some((ss) => ss.values === 'all')
        ) {
            return getAllCategoriesByDepartmentsAndSuperCategories(filters.departments, filters.superCategories);
        } else {
            return allExceptProductFieldsCategories;
        }
    }, [filters, allExceptProductFieldsCategories]);

    async function handleChange(e) {
        if (filterType.type === 'marketCharacteristics') {
            onChange?.(e);
            return;
        }
        if (Array.isArray(e.value)) {
            let runConfigMarkets: RunConfigMarket[] = e.value.map((marketName) =>
                getRunConfigMarketForMarketName(
                    marketName,
                    categories,
                    true,
                    filters?.categories?.summedSelections.length ? true : false,
                    filters?.categories?.summedSelections.map((item) => item.values).flat(),
                    filters?.categories.values as string[],
                    inSummedMode,
                )
            ).filter(Boolean) as RunConfigMarket[];
            const newMarketSet: RunConfigMarket[] = removeDuplicateMarkets([...runConfigMarkets]).sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            setMarkets(newMarketSet);
            onChange?.({
                ...e,
                value: newMarketSet,
            });
        } else if (e.value === 'all' && options.length > 0) {
            let runConfigMarkets: RunConfigMarket[] = options
                .filter((option) => !option.disabled)
                .map((marketOption) =>
                    getRunConfigMarketForMarketName(
                        marketOption.value,
                        categories,
                        true,
                        filters?.categories?.summedSelections.length ? true : false,
                        filters?.categories?.summedSelections.map((item) => item.values).flat(),
                        filters?.categories.values as string[],
                        inSummedMode,
                    )
                ).filter(Boolean) as RunConfigMarket[];
            const newMarketSet: RunConfigMarket[] = removeDuplicateMarkets([...runConfigMarkets]).sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            // this is needed because the `options` may include the previously selected options
            // for example first selecting from 'Accounts' hierarchy 
            // then switching to 'Channels' and selecting all will clear previous selections
            setMarkets((prev)=>{
                const latestSelectedMarkets = unionBy(newMarketSet, prev, 'name')
                onChange?.({
                    ...e,
                    value: latestSelectedMarkets,
                });
                return latestSelectedMarkets
            });
        }
    }

    function handleMarketTreeChange(e) {
        setMarkets(e.value);
        onChange?.({
            ...e,
            value: e.value,
        });
    }

    function handleMarketTreeApply(e) {
        onApply?.({
            ...e,
            value: markets,
        });
    }

    function removeDuplicateMarkets(markets: RunConfigMarket[]): RunConfigMarket[] {
        const stringifiedMarkets = markets.filter(Boolean).map((market) => JSON.stringify(market));
        const marketSet = new Set(stringifiedMarkets);
        const marketArray = Array.from(marketSet).map((market) => JSON.parse(market));

        return marketArray;
    }

    function handleApply(e) {
        if (filterType.type === 'marketCharacteristics') {
            onApply?.(e);
            return;
        }
        if (Array.isArray(e.value)) {
            let runConfigMarkets: RunConfigMarket[] = e.value.map((marketName) =>
                getRunConfigMarketForMarketName(
                    marketName,
                    categories,
                    true,
                    filters?.categories?.summedSelections.length ? true : false,
                    filters?.categories?.summedSelections.map((item) => item.values).flat(),
                    filters?.categories.values as string[],
                    inSummedMode,
                )
            ).filter(Boolean) as RunConfigMarket[];
            setMarkets(runConfigMarkets);
            onApply?.({
                ...e,
                value: runConfigMarkets,
            });
        } else {
            let runConfigMarkets: RunConfigMarket[] = options
                .filter(({ disabled }) => !disabled)
                .map((marketOption) =>
                    getRunConfigMarketForMarketName(
                        marketOption.value,
                        categories,
                        true,
                        filters?.categories?.summedSelections.length ? true : false,
                        filters?.categories?.summedSelections.map((item) => item.values).flat(),
                        filters?.categories.values as string[],
                        inSummedMode,
                    )
                ).filter(Boolean) as RunConfigMarket[];
            // this is needed because the `options` may include the previously selected options
            // for example first selecting from 'Accounts' hierarchy 
            // then switching to 'Channels' and selecting all will clear previous selections
            setMarkets((prev)=>{
                const latestSelectedMarkets = unionBy(runConfigMarkets, prev,'name')
                onApply?.({
                    ...e,
                    value: latestSelectedMarkets,
                });
                return latestSelectedMarkets
            });
        }
    }

    return (
        <>
            {activeMarketFolderTab === 'markets' ? (
                <>
                    {filterType.type === 'all' || filterType.type === 'accounts' || enableLimitedMarketSubscription ? (
                        <DodMarketTree
                            className={classnames(baseClassName, className)}
                            value={markets}
                            limit ={limit}
                            onLimitExceed = {()=>alertSelectionLimitExceed("market",limit)}
                            normalizedValue={normalizedValue === 'all' ? undefined : normalizedValue}
                            onChange={handleMarketTreeChange}
                            onApply={handleMarketTreeApply}
                            loading={loading}
                            categories={categories}
                            filterType={filterType.type as any}
                            title="Make Your Market Selections"
                            filterText={filterText}
                            options={options}
                            onOptionChange={handleChange}
                            onOptionApply={handleApply}
                            onFilterChange={filterValueConfigs[filterType.type].filterValues}
                            hideTotalUSMarkets={filterType.type === 'accounts'}
                            hideGeographicMarkets={filterType.type === 'accounts'}
                            filters={filters}  
                            inSumSelectionMode={inSummedMode}     
                            {...props}
                        />
                    ) : (
                        <DodFilterValuePicker
                            className={classnames(baseClassName, className)}
                            options={options}
                            loading={loading}
                            onChange={handleChange}
                            onApply={handleApply}
                            limit = {limit}
                            onLimitExceed = {()=>alertSelectionLimitExceed("market",limit)}
                            value={normalizedValue}
                            onFilterChange={filterValueConfigs[filterType.type].filterValues}
                            filterText={filterText}
                            enableExplicitAllSelection={true}
                            dodWizardStep="market"
                            displaySelectAllOption = {!inSummedMode}             
                            {...props}
                        />
                    )}
                </>
            ) : (
                <>
                    <DodFilterValuePicker
                        className={classnames(baseClassName, className)}
                        options={options}
                        loading={loading}
                        limit = {limit}
                        onLimitExceed = {()=>alertSelectionLimitExceed("market",limit)}
                        onChange={handleChange}
                        onApply={handleApply}
                        value={normalizedValue}
                        onFilterChange={
                            activeMarketFolderTab === 'search'
                                ? undefined
                                : filterValueConfigs[filterType.type].filterValues
                        }
                        filterText={filterText}
                        enableExplicitAllSelection={true}
                        dodWizardStep="market"
                        displaySelectAllOption = {!inSummedMode}
                        filterType={filterType.type as any}
                        {...props}
                    />
                </>
            )}
        </>
    );
}

export default DodMarketFilterValuePicker;